import { PlusButton } from "@components/buttons/plus";
import { SHStack } from "@components/design-systems";
import { useUserPermissions } from "@hooks/userUserPermission";
import {
  AnalysisFeeCalculatorDTO,
  FamilyMemberAccountDTO,
  FamilyMemberDTO,
  FeeCalculatorRenderFieldProps,
} from "@models/platform-analysis/entities/steps/fee";
import { Collapse } from "@mui/material";
import { RootState } from "@redux/store";
import { omitFieldFromObject } from "@utils";
import { isEmpty } from "lodash";
import { memo, useEffect, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { TransitionGroup } from "react-transition-group";
import { AnalysisFeeCalculatorStepConfigs as Configs } from "../../../configs";
import { FamilyMemberCard } from "./components/card";

export const FamilyMemberAccountsSetup = memo(
  ({ disabled }: FeeCalculatorRenderFieldProps) => {
    const storedFamilyMembersData = useSelector(
      (state: RootState) =>
        state.platformAnalysis?.feeCalculator?.familyMembers,
    );
    const { isSupplierGroup } = useUserPermissions();
    const isReview = !isSupplierGroup;

    const { control } = useFormContext<AnalysisFeeCalculatorDTO>();
    const {
      fields: familyMembersFields,
      replace: replaceFamilyMembers,
      update: updateFamilyMember,
    } = useFieldArray({
      control,
      name: "familyMembers",
      keyName: "fieldId",
    });

    const totalAccountsCreated: number = useMemo(() => {
      return familyMembersFields.reduce((total, member) => {
        return (
          total +
          (member.familyMemberAccounts ? member.familyMemberAccounts.length : 0)
        );
      }, 0);
    }, [familyMembersFields]);

    const handleOnAddFamilyEntity = () => {
      const newFamilyMember = new FamilyMemberDTO();
      newFamilyMember.order = familyMembersFields.length + 1;
      const newArray = [...familyMembersFields, newFamilyMember];
      replaceFamilyMembers(newArray);
    };

    const handleOnRemoveFamilyEntity = (id?: string | null) => {
      if (!id) return;
      const filteredFamilyMembers = familyMembersFields.filter(
        (member) => member.id !== id,
      );
      replaceFamilyMembers(filteredFamilyMembers);
    };

    const handleOnAddAccount = ({
      memberIndex,
      isIDPS,
    }: {
      memberIndex: number;
      isIDPS: boolean;
    }) => {
      if (totalAccountsCreated > Configs.MaximumFamilyMemberAccounts) return;

      const targetFamilyMember = familyMembersFields[memberIndex];
      if (!targetFamilyMember || !targetFamilyMember.familyMemberAccounts)
        return;

      const newAccount = new FamilyMemberAccountDTO();
      newAccount.isIDPS = isIDPS;

      targetFamilyMember.familyMemberAccounts.push(newAccount);
      updateFamilyMember(
        memberIndex,
        omitFieldFromObject(targetFamilyMember, "fieldId"),
      );
    };

    const handleOnRemoveAccount = ({
      memberIndex,
      accountId,
    }: {
      memberIndex: number;
      accountId: string;
      isIDPS: boolean;
    }) => {
      const targetFamilyMember = familyMembersFields[memberIndex];

      if (!targetFamilyMember || !targetFamilyMember.familyMemberAccounts)
        return;

      targetFamilyMember.familyMemberAccounts =
        targetFamilyMember.familyMemberAccounts.filter(
          (account) => account.id !== accountId,
        );
      updateFamilyMember(
        memberIndex,
        omitFieldFromObject(targetFamilyMember, "fieldId"),
      );
    };

    const handleOnCardNameChange = ({
      memberIndex,
      newName,
    }: {
      memberIndex: number;
      newName: string;
    }) => {
      const targetFamilyMember = familyMembersFields[memberIndex];
      if (!targetFamilyMember) return;

      targetFamilyMember.name = newName;
      updateFamilyMember(
        memberIndex,
        omitFieldFromObject(targetFamilyMember, "fieldId"),
      );
    };

    useEffect(() => {
      if (isEmpty(storedFamilyMembersData)) return;
      replaceFamilyMembers(storedFamilyMembersData || []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedFamilyMembersData]);

    const shouldDisableCreateNewMember = useMemo(() => {
      return (
        disabled ||
        familyMembersFields.length >= Configs.MaximumFamilyMemberAccounts ||
        totalAccountsCreated > Configs.MaximumFamilyMemberAccounts
      );
    }, [disabled, familyMembersFields.length, totalAccountsCreated]);

    return (
      <SHStack
        spacing={3}
        direction="column"
        sx={{ width: { xs: "100%", md: 520 } }}
      >
        <TransitionGroup
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
          }}
        >
          {familyMembersFields.map((entity, index) => (
            <Collapse key={entity.id}>
              <FamilyMemberCard
                disabled={disabled}
                memberIndex={index}
                memberInfo={entity}
                canRemove={!isReview && index !== 0}
                maxAccountsReached={
                  totalAccountsCreated > Configs.MaximumFamilyMemberAccounts
                }
                onAddAccount={({ isIDPS }) => {
                  handleOnAddAccount({ memberIndex: index, isIDPS });
                }}
                onRemove={() => handleOnRemoveFamilyEntity(entity.id)}
                onBalanceRemove={({ isIDPS, accountId }) => {
                  handleOnRemoveAccount({
                    memberIndex: index,
                    accountId,
                    isIDPS,
                  });
                }}
                onNameChange={(newName) =>
                  handleOnCardNameChange({ memberIndex: index, newName })
                }
              />
            </Collapse>
          ))}
        </TransitionGroup>

        {!isReview && (
          <SHStack alignItems={"center"}>
            <PlusButton
              onClick={handleOnAddFamilyEntity}
              disabled={shouldDisableCreateNewMember}
            >
              Family member/entity
            </PlusButton>
          </SHStack>
        )}
      </SHStack>
    );
  },
);
